/* tslint:disable:no-trailing-whitespace */

import { IGroupButton } from './GroupButton';

/**
 * An externally-exposed representation of a rule, living as an element in IQuery.where.
 */
export interface IRule {
    /**
     * The auto-generated id of the rule.  This is populated by Helper.prepRules and stripped out 
     * by Helper.unprepRules().  This attribute is used extensively to uniquely identify one rule
     * from another.
     */
    $$ruleid?: string;
    /**
     * True if the rule is being edited as a new rule that should not yet be returned to consuming
     * components. This attribute is stripped out by Helper.unprepRules(). False otherwise.
     */
    $$isNew?: boolean;
    /**
     * The object representation of the buttons that should be drawn on the left-hand side for this rule.
     * Group buttons are calculated to appear in the middle of the set of direct rules and child groups.
     * This is populated by Helper.prepGroupButtons() and stripped out by Helper.unprepRules().  Each 
     * array element corresponds to the index of the group depth to the left of the rule.  So, if the
     * deepest supported group depth is 3, then the 0 index corresponds to the first group area and 
     * the 2 index corresponds to the third group area.  Suppose we are showing a group button at depth 3,
     * then the group button array could look like this:
     * $$groupButtons: [
     *  { groupId: 'A', condition: 'AND' }, // do not show group button
     *  { groupId: 'B', condition: 'AND' }, // do not show group button
     *  { groupId: 'C', condition: 'AND', showGroupButton: true },// show group button
     * ]
     */
    $$groupButtons?: IGroupButton[];
    /**
     * The groupIds of the rule.  Each rule should have at least the default group id in the 0 index
     * of the groupIds array.  Each array element corresponds to the index of the group from top to
     * bottom.  Suppose a rule is part of group C, nested under group B, nested under default group A.
     * In this case the groupIds array would be:
     * groupIds: ['A', 'B', 'C']
     */
    groupIds?: string[];
    /**
     * The attribute of the rule.  Required in order to proceed with population of the operator.
     */
    attribute?: IAttribute;
    /**
     * The operator of the rule.
     */
    operator?: string;
    /**
     * The relationship attribute that drives the available child attributes. This essentially
     * maps to an EEDM model.
     */
    relationshipAttributePath?: IAttribute[];
    /**
     * The first value of the rule.
     */
    value?: ValueType;
    /**
     * The second value of the rule (if the attribute-operator combination allows a second value).
     * See IOperator.requiresValueB attribute.
     */
    valueB?: ValueType;
}

/**
 * The type of the value field on IRule.
 */
export type ValueType = Date | string | number | ILookupValue | string[];

/**
 * The lookup value type.
 */
export interface ILookupValue {
    id: string;
    title: string;
}

/**
 * The externally-exposed attribute type of an attribute, available in IAttribute.type.
 */
export enum AttributeType {
    Enum = 'enum',
    Date = 'date',
    Int = 'int',
    String = 'string',
    Lookup = 'lookup',
    Relationship = 'relationship',
    MultiSelectEnum = 'multiSelectEnum',
    RadioChoiceEnum = 'radioChoiceEnum'
}

/**
 * The externally-exposed operator, available in IAttribute.operators.
 */
export interface IOperator {
    label: string;
    value: string;
    requiresValueB?: boolean;
    hideValueInput?: boolean;
}

/**
 * The externally-exposed attribute information that drives the available corresponding
 * operators, input type, and enum options.
 */
export interface IAttribute {
    enum?: IEnum[];
    description?: string;
    label: string;
    lookupValueMinLength?: number;
    lookupValueLabel?: string;
    operators: IOperator[];
    path?: { label: string, value?: string }[];
    type: AttributeType;
    value: string;
    validate?: (rule: IRule) => string;
    isLoading?: boolean;
}

/**
 * The externally-exposed enum, available in IAttribute.enum.  These are options available
 * for a rule that is of type AttributeType.Enum.
 */
export interface IEnum {
    label: string;
    value: string;
}

export interface IQueryRuleComponentsProps {
    selectAttributeComponent: {
        error: boolean,
        helperText: string
        persistTooltip: boolean,
    }
}
